import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol.js';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol.js';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol.js';
import TextSymbol from '@arcgis/core/symbols/TextSymbol.js';

export const SELECTION_FILL = new SimpleFillSymbol({
  color: [0, 180, 220, 0.3],
  outline: { color: [0, 140, 200, 1], width: 2.5 },
});

export const SELECTION_LINE = new SimpleLineSymbol({
  color: [0, 160, 210, 1],
  width: 4,
});

export const SELECTION_POINT = new SimpleMarkerSymbol({
  style: 'circle',
  color: [0, 180, 220, 0.9],
  size: 12,
  outline: { color: [244, 236, 221, 1], width: 2 },
});

export const HIGHLIGHT_FILL = new SimpleFillSymbol({
  color: [255, 184, 0, 0.32],
  outline: { color: [255, 130, 0, 1], width: 1.6 },
});

export const HIGHLIGHT_LINE = new SimpleLineSymbol({
  color: [255, 130, 0, 1],
  width: 3,
});

export const HIGHLIGHT_POINT = new SimpleMarkerSymbol({
  style: 'circle',
  color: [255, 184, 0, 0.95],
  size: 10,
  outline: { color: [120, 60, 0, 1], width: 1.5 },
});

export const BUFFER_FILL = new SimpleFillSymbol({
  color: [102, 142, 207, 0.18],
  outline: { color: [80, 120, 200, 0.95], width: 1.4 },
});

export const SKETCH_FILL = new SimpleFillSymbol({
  color: [200, 80, 200, 0.18],
  outline: { color: [180, 60, 180, 1], width: 1.5 },
});

export const SKETCH_LINE = new SimpleLineSymbol({
  color: [180, 60, 180, 1],
  width: 2.5,
});

export const SKETCH_POINT = new SimpleMarkerSymbol({
  style: 'circle',
  color: [200, 80, 200, 0.95],
  size: 11,
  outline: { color: [255, 255, 255, 1], width: 2 },
});

export const SKETCH_FILL_HIGHLIGHT = new SimpleFillSymbol({
  color: [255, 184, 0, 0.32],
  outline: { color: [255, 130, 0, 1], width: 2.5 },
});

export const SKETCH_LINE_HIGHLIGHT = new SimpleLineSymbol({
  color: [255, 130, 0, 1],
  width: 3.5,
});

export const SKETCH_POINT_HIGHLIGHT = new SimpleMarkerSymbol({
  style: 'circle',
  color: [255, 130, 0, 0.95],
  size: 13,
  outline: { color: [255, 255, 255, 1], width: 2 },
});

export const MARKER_POINT = new SimpleMarkerSymbol({
  style: 'circle',
  color: [255, 92, 60, 0.95],
  size: 12,
  outline: { color: [255, 255, 255, 1], width: 2 },
});

const MEASURE_ACCENT: [number, number, number] = [29, 145, 192];
const MEASURE_HIGHLIGHT: [number, number, number] = [255, 130, 0];

export const MEASURE_LINE = new SimpleLineSymbol({
  color: [...MEASURE_ACCENT, 1],
  width: 3,
});

export const MEASURE_LINE_HIGHLIGHT = new SimpleLineSymbol({
  color: [...MEASURE_HIGHLIGHT, 1],
  width: 4,
});

export const MEASURE_FILL = new SimpleFillSymbol({
  color: [...MEASURE_ACCENT, 0.18],
  outline: { color: [...MEASURE_ACCENT, 1], width: 2 },
});

export const MEASURE_FILL_HIGHLIGHT = new SimpleFillSymbol({
  color: [...MEASURE_HIGHLIGHT, 0.28],
  outline: { color: [...MEASURE_HIGHLIGHT, 1], width: 2.5 },
});

export function measureLabelSymbol(text: string): TextSymbol {
  return new TextSymbol({
    text,
    color: [255, 255, 255, 1],
    haloColor: [...MEASURE_ACCENT, 1],
    haloSize: 1.25,
    font: { size: 12, weight: 'bold', family: 'Inter, system-ui, sans-serif' },
    yoffset: 0,
    xoffset: 0,
  });
}

export type SelectionRole = 'selection' | 'highlight';

export function symbolForGeometry(
  geometryType: __esri.Geometry['type'] | undefined,
  role: SelectionRole = 'selection',
) {
  switch (geometryType) {
    case 'polyline':
      return role === 'highlight' ? HIGHLIGHT_LINE : SELECTION_LINE;
    case 'point':
    case 'multipoint':
      return role === 'highlight' ? HIGHLIGHT_POINT : SELECTION_POINT;
    case 'polygon':
    case 'extent':
    default:
      return role === 'highlight' ? HIGHLIGHT_FILL : SELECTION_FILL;
  }
}
